// Catalog.ID Client — Registration Payment Flow Helpers
// ============================================================================
// Shared between the personal and organization registration wizards. Handles:
//  - Nonce generation
//  - Talking to the Cloudflare Worker (create-payment + payment-status polling)
//  - Persisting wizard state across the Stripe redirect via sessionStorage
//  - The dev-only VITE_PAYMENT_TEST_HOOK that skips Stripe entirely and mints
//    an ES256 proof locally with a test signing key. Tree-shaken in production
//    builds where the env vars are unset.

import type {
    CreatePaymentRequest,
    CreatePaymentResponse,
    PaymentStatusResponse,
    RegistrationType,
    VoucherLockSpec,
} from '../api/payment';
import { purchaseVouchers } from '../api/voucher';
import { fromBase64 } from '../crypto/aes';
import { getApiBase } from '../state/dashboard-context';

// ── Configuration ───────────────────────────────────────────────────────────

/**
 * Runtime registration configuration served by the CID server
 * (`GET /api/registration/config`) so deployment posture lives in server
 * config, not client build-time flags: whether payment is required at all
 * (`Payment:Mode` on the server), and the Bitcash v2 ledger the member's
 * browser talks to (`Bitcash:LedgerUrl`, `Bitcash:NetworkId`; apps/bitcash/docs/plans/bitcash-v2.md M12).
 *
 * FAIL-CLOSED: any fetch/parse error yields `{ paymentRequired: true,
 * ledgerUrl: null, registrationMode: 'invitation', fromServer: false }` —
 * an unreachable endpoint can never waive a fee or open registration. A node's
 * answer is cached for the session; a failed read is not, so the next caller
 * asks again. The wizard awaits this before the Payment step.
 */
export interface RegistrationConfig {
    paymentRequired: boolean;
    /** The ledger's origin, or null when the node names none — and then no ledger call is made at all:
     *  there is no built-in default (M12). */
    ledgerUrl: string | null;
    /** The network the ledger serves (`live`, `test`, `dev`): refs and view codes are bound to it. */
    networkId: NetworkId | null;
    /** The BIT an owner-minted code of each type carries, or null when the node gifts nothing
     *  (the backoffice offers its gift toggle only then). */
    gifts: GiftOffer | null;
    /** Origin of the node's standalone web client (nginx vhost in production),
     *  used for printed links that must open the app; null when the node
     *  announces none (dev), in which case the dashboard helper origin serves. */
    siteBaseUrl: string | null;
    /**
     * The bits a voucher may carry (registration-wallets D1). DISPLAY MIRRORS
     * ONLY: the WORKER owns these constants and enforces them at checkout, and
     * this node does not even learn how many bits were bought. They are here so
     * the storefront can size a field without a second round trip — the
     * authoritative pair, including the euro rate, is the Worker's own
     * `GET /api/checkout/bits-config`.
     */
    bits: BitsBounds;
    /** The terms the account's default AUTH token is minted with — by the
     *  registration wizards, the key-grant handler and Settings: D27's caps, whole
     *  BIT, and the service types it pays. Null when the node names none —
     *  go-live configuration (apps/bitcash/docs/plans/bitcash-v2.md M12, D15
     *  ruling) — and then no default token is issued. */
    defaultToken: DefaultTokenTerms | null;
    /** Body length of an `RGV-` code (D25), so a paste box can size itself
     *  against the server it is actually talking to. */
    voucherCodeLength: number;
    /** The node runs the Development preset. Only an explicit `true` sets it. */
    devPreset: boolean;
    /**
     * How the node takes in new members (docs/cid-governance.md M12): `open`, or
     * `invitation` — only a voucher minted there, an invitation a voucher backs,
     * or a signed delegate invitation. A node without its own certificate says
     * `invitation` whatever it was configured (D14). Only an explicit `open`
     * opens; see `auth/registration-gate.ts` for what each mode offers.
     */
    registrationMode: RegistrationMode;
    /** True when the node answered; false on the fail-closed defaults, which the
     *  pages explain differently ("could not reach" rather than "invitation only"). */
    fromServer: boolean;
}

/**
 * `GET /api/registration/config` → `defaultToken` (`Voucher:DefaultToken:*`): the
 * caps (D27) and the service types the member's default token is minted with.
 * The token is UNBOUND — no payee, so it pays Catalog services at any operator —
 * and an unbound token without service types would also pay content, so the
 * list is part of the terms: never empty (apps/docs/plans/second-node-and-bitcash-open-issues.md
 * §2.1, §9.3 D-a).
 */
export interface DefaultTokenTerms {
    dailyCap: number;
    monthlyCap: number;
    serviceTypes: string[];
}

/** `GET /api/registration/config` → `registrationMode` (M12). */
export type RegistrationMode = 'open' | 'invitation';

/** A ledger network id (apps/bitcash/docs/plans/bitcash-v2.md D49). */
export type NetworkId = 'live' | 'test' | 'dev';

/** `GET /api/registration/config` → `gifts`: BIT per owner-minted code, null where none is given. */
export interface GiftOffer {
    personal: number | null;
    organization: number | null;
    organizationFounder: number | null;
    delegate: number | null;
}

function readGifts(raw: unknown): GiftOffer | null {
    if (typeof raw !== 'object' || raw === null) return null;
    const o = raw as Record<string, unknown>;
    const bit = (v: unknown): number | null => typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : null;
    return { personal: bit(o.personal), organization: bit(o.organization), organizationFounder: bit(o.organizationFounder), delegate: bit(o.delegate) };
}

/** Mirrors {@link import('./voucher-purchase-view').BitsBounds}; declared here
 *  because the config is what carries it and `payment-flow` must not depend on
 *  a view. */
export interface BitsBounds {
    min: number;
    max: number;
    default: number;
}

/** What the node says when it says nothing: the plan's own D1/D27 numbers.
 *  Used on a fail-closed config, where the Worker remains the enforcer. */
const DEFAULT_BITS_BOUNDS: BitsBounds = { min: 100_000, max: 10_000_000, default: 100_000 };
/** A bare v2 service type (D55), as a token's `caps.serviceTypes` takes it. */
const SERVICE_TYPE = /^[a-z][a-z0-9-]{0,31}$/;
/** The key-grant handler's bound on a scope list (MAX_SCOPE_LIST); the node refuses more too. */
const MAX_SERVICE_TYPES = 32;

/** The default token's terms as the node publishes them: both caps whole BIT, monthly at least
 *  daily, and a non-empty list of distinct service types — or none at all. */
function readDefaultTokenCaps(raw: unknown): DefaultTokenTerms | null {
    if (typeof raw !== 'object' || raw === null) return null;
    const { dailyCap, monthlyCap, serviceTypes } = raw as Record<string, unknown>;
    if (typeof dailyCap !== 'number' || typeof monthlyCap !== 'number') return null;
    if (!Number.isSafeInteger(dailyCap) || !Number.isSafeInteger(monthlyCap) || dailyCap <= 0 || monthlyCap < dailyCap) return null;
    if (!Array.isArray(serviceTypes) || serviceTypes.length === 0 || serviceTypes.length > MAX_SERVICE_TYPES) return null;
    if (!serviceTypes.every((t): t is string => typeof t === 'string' && SERVICE_TYPE.test(t))) return null;
    if (new Set(serviceTypes).size !== serviceTypes.length) return null;
    return { dailyCap, monthlyCap, serviceTypes: [...serviceTypes] };
}
const DEFAULT_VOUCHER_CODE_LENGTH = 16;

/** Read one `{min,max,default}` block, falling back per FIELD: a node that
 *  answers a partial object must not silently widen the range. */
function readBitsBounds(raw: unknown): BitsBounds {
    const o = (raw ?? {}) as Record<string, unknown>;
    const num = (v: unknown, fallback: number): number =>
        typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v) : fallback;
    return {
        min: num(o.min, DEFAULT_BITS_BOUNDS.min),
        max: num(o.max, DEFAULT_BITS_BOUNDS.max),
        default: num(o.default, DEFAULT_BITS_BOUNDS.default),
    };
}

let cachedRegistrationConfig: RegistrationConfig | null = null;

export async function fetchRegistrationConfig(): Promise<RegistrationConfig> {
    if (cachedRegistrationConfig) return cachedRegistrationConfig;
    let config: RegistrationConfig = {
        paymentRequired: true,
        ledgerUrl: null,
        networkId: null,
        gifts: null,
        siteBaseUrl: null,
        bits: DEFAULT_BITS_BOUNDS,
        defaultToken: null,
        voucherCodeLength: DEFAULT_VOUCHER_CODE_LENGTH,
        devPreset: false,
        registrationMode: 'invitation',
        fromServer: false,
    };
    try {
        const resp = await fetch(`${getApiBase()}/api/registration/config`);
        if (resp.ok) {
            const body = await resp.json() as {
                paymentRequired?: unknown; ledgerUrl?: unknown; networkId?: unknown;
                gifts?: unknown; siteBaseUrl?: unknown;
                bits?: unknown; defaultToken?: unknown; voucherCodeLength?: unknown;
                devPreset?: unknown; registrationMode?: unknown;
            };
            config = {
                // Only an explicit false waives — anything malformed stays required.
                paymentRequired: body.paymentRequired !== false,
                ledgerUrl: typeof body.ledgerUrl === 'string' && body.ledgerUrl !== ''
                    ? body.ledgerUrl.replace(/\/+$/, '') : null,
                networkId: body.networkId === 'live' || body.networkId === 'test' || body.networkId === 'dev'
                    ? body.networkId : null,
                gifts: readGifts(body.gifts),
                siteBaseUrl: typeof body.siteBaseUrl === 'string' && body.siteBaseUrl !== ''
                    ? body.siteBaseUrl.replace(/\/+$/, '') : null,
                bits: readBitsBounds(body.bits),
                defaultToken: readDefaultTokenCaps(body.defaultToken),
                voucherCodeLength: typeof body.voucherCodeLength === 'number' && body.voucherCodeLength > 0
                    ? Math.round(body.voucherCodeLength) : DEFAULT_VOUCHER_CODE_LENGTH,
                devPreset: body.devPreset === true,
                // Only an explicit 'open' opens (M12) — a node that says nothing,
                // or anything else, registers by invitation only.
                registrationMode: body.registrationMode === 'open' ? 'open' : 'invitation',
                fromServer: true,
            };
        }
    } catch {
        // Network error → fail closed (payment required, invitation only).
    }
    // A failed read is answered closed but not remembered: the next caller asks
    // the node again rather than keeping the page closed until a reload.
    if (config.fromServer) cachedRegistrationConfig = config;
    return config;
}

/** Test seam: reset the cached config (unit tests only). */
export function resetRegistrationConfigCache(): void {
    cachedRegistrationConfig = null;
}

/**
 * The ledger's origin, from the node's runtime config once it answered
 * (`Bitcash:LedgerUrl`), or null — before that, and on a node that names none.
 * There is NO build-time or built-in fallback (apps/bitcash/docs/plans/bitcash-v2.md M12): a client
 * that does not know its ledger makes no ledger call, so it can never reach a
 * ledger, or a pilot, nobody configured.
 */
export function getLedgerUrl(): string | null {
    return cachedRegistrationConfig?.ledgerUrl ?? null;
}

/** The checkout routes the pilot Worker served (track C, M16): dormant behind `paymentRequired`,
 *  which a node on the v2 ledger never answers outside Development. */
function checkoutBase(): string {
    const ledger = getLedgerUrl();
    if (!ledger) throw new Error('Payments are not available on this server. Nothing has been charged.');
    return ledger;
}

/**
 * The success URL Stripe redirects to after payment. The Worker substitutes
 * `{REGISTRATION_ID}` with our generated registration id (not Stripe's session
 * id — we key everything on ours).
 *
 * `popupReturn` adds a marker the SPA route dispatch uses to recognise the
 * dashboard popup window (whose own sessionStorage is empty) and render a
 * "you can close this window" page instead of trying to resume registration
 * there — the parent pane already drives completion by polling.
 */
export function getSuccessUrl(popupReturn = false): string {
    const base = `${window.location.origin}/auth/registration-complete?registrationId={REGISTRATION_ID}`;
    return popupReturn ? `${base}&popupReturn=1` : base;
}

export function getCancelUrl(popupReturn = false): string {
    const base = `${window.location.origin}/register`;
    return popupReturn ? `${base}?popupReturn=1` : base;
}

// ── Nonce ───────────────────────────────────────────────────────────────────

/** Generate a fresh 32-byte random nonce, base64url-encoded (no padding). */
export function freshNonce(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
}

function base64UrlEncode(bytes: Uint8Array): string {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

// ── Worker calls ────────────────────────────────────────────────────────────

/**
 * A `create-payment` refusal in words (registration-wallets §4.3, and the
 * Worker's own `{ error, code }` contract).
 *
 * Branch on the CODE, never on the prose. Three codes mean the operator cannot
 * sell bits right now: nobody's fault, worth waiting out, and the only ones a
 * buyer should be told to retry —
 *
 *   * `inventory_credit_exhausted` (409, Bitcash Ledger Specification v2 §6):
 *     the operator's open inventory debt has reached its credit limit. That is
 *     a SETTLEMENT BACKLOG, cleared when the operator settles with the Bitcash
 *     Administrator; it is never worded as stock;
 *   * `inventory` and `allotment`: the pilot Worker's stock refusals, retired
 *     by §12 and kept until the pilot Worker stops sending them.
 *
 * Every other code is a bug on this side (a malformed lock key, a duplicate, a
 * bits figure out of range) and must NOT be dressed up as bad luck, or a buyer
 * loops on a retry that can never work.
 */
export function checkoutRefusalMessage(
    code: string | null, workerMessage: string, status = 400,
): string {
    switch (code) {
        case 'inventory_credit_exhausted':
            return 'The operator has to settle its account with the Bitcash network before it can '
                + 'sell more bits, so these codes cannot be issued right now. Nothing has been '
                + 'charged — please try again later.';
        case 'inventory':
        case 'allotment':
            return 'The operator\'s wallet is out of stock, so these codes cannot be issued right '
                + 'now. Nothing has been charged — please try again later.';
        case 'bad_bits':
        case 'bad_founder_bits':
            return 'That number of bits is outside the range this operator allows. '
                + `Adjust it and try again. (${workerMessage})`;
        case 'unknown_operator':
            return 'This operator has no wallet registered to sell from, so nothing can be bought '
                + 'here yet. Nothing has been charged.';
        default:
            if (status === 503) {
                return 'Payments are not available on this server at the moment. Nothing has been '
                    + 'charged.';
            }
            // An unrecognised code: the Worker's own sentence is more specific
            // than anything invented here.
            return `This purchase could not be started: ${workerMessage}`;
    }
}

export async function createPayment(req: CreatePaymentRequest): Promise<CreatePaymentResponse> {
    const resp = await fetch(`${checkoutBase()}/api/checkout/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
    });
    if (!resp.ok) {
        const text = await resp.text();
        let code: string | null = null;
        let message = text.slice(0, 200);
        try {
            const body = JSON.parse(text) as { error?: unknown; code?: unknown };
            if (typeof body.code === 'string') code = body.code;
            if (typeof body.error === 'string' && body.error) message = body.error;
        } catch { /* not JSON — the raw text stands in */ }
        throw new Error(checkoutRefusalMessage(code, message, resp.status));
    }
    return await resp.json() as CreatePaymentResponse;
}

export async function getPaymentStatus(registrationId: string, nonce: string): Promise<PaymentStatusResponse> {
    const url = `${checkoutBase()}/api/checkout/payment-status`;
    // POST + JSON body — keeps the nonce out of URL-level logs / caches.
    const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registrationId, nonce }),
    });
    if (resp.status === 403) {
        // 403 means registration not found OR nonce mismatch. We can't
        // distinguish. Surface as "error" so the wizard offers a retry.
        return { status: 'error', error: 'Payment record not found or nonce mismatch (HTTP 403)' };
    }
    if (!resp.ok) {
        const text = await resp.text();
        return { status: 'error', error: `payment-status failed (${resp.status}): ${text.slice(0, 200)}` };
    }
    return await resp.json() as PaymentStatusResponse;
}

/**
 * Poll `payment-status` with exponential backoff (1s → 2s → 4s → 8s → 8s → 8s,
 * ~31s total) until we get a `complete` or `error` response. Returns null on
 * abort. Times out after the schedule completes — caller can retry or surface
 * a "still processing" message.
 */
export async function pollUntilComplete(
    registrationId: string,
    nonce: string,
    onTick: (response: PaymentStatusResponse, attempt: number) => void = () => {},
    abortSignal?: AbortSignal,
): Promise<PaymentStatusResponse> {
    const delays = [1000, 2000, 4000, 8000, 8000, 8000];
    let last: PaymentStatusResponse = { status: 'pending' };
    for (let i = 0; i < delays.length; i++) {
        if (abortSignal?.aborted) return last;
        // First attempt fires immediately, no leading delay.
        if (i > 0) await new Promise((r) => setTimeout(r, delays[i - 1]));
        try {
            last = await getPaymentStatus(registrationId, nonce);
        } catch (e) {
            last = { status: 'error', error: e instanceof Error ? e.message : String(e) };
        }
        onTick(last, i + 1);
        if (last.status === 'complete' || last.status === 'error') return last;
    }
    return last;
}

/**
 * Long-poll `payment-status` until the worker reports a terminal status
 * (`complete`/`error`), the caller aborts, or `maxDurationMs` elapses. Used by
 * the dashboard popup flow (see `startPopupCheckoutAndAwaitProof`), where the
 * poll must span the entire live payment — card entry, 3-D Secure, iDEAL bank
 * hop — i.e. minutes, not the fixed ~31s schedule of `pollUntilComplete` (which
 * stays the right fit for the standalone redirect-back flow, where payment is
 * already done by the time the completion step polls). Always polls at least
 * once; returns the last status seen (`pending` on abort/timeout).
 */
export async function pollPaymentUntilSettled(
    registrationId: string,
    nonce: string,
    opts: {
        onTick?: (response: PaymentStatusResponse, attempt: number) => void;
        abortSignal?: AbortSignal;
        intervalMs?: number;
        maxDurationMs?: number;
    } = {},
): Promise<PaymentStatusResponse> {
    const intervalMs = opts.intervalMs ?? 2500;
    const maxDurationMs = opts.maxDurationMs ?? 600_000;
    const deadline = Date.now() + maxDurationMs;
    let attempt = 0;
    let last: PaymentStatusResponse = { status: 'pending' };
    while (!opts.abortSignal?.aborted) {
        attempt++;
        try {
            last = await getPaymentStatus(registrationId, nonce);
        } catch (e) {
            last = { status: 'error', error: e instanceof Error ? e.message : String(e) };
        }
        opts.onTick?.(last, attempt);
        if (last.status === 'complete' || last.status === 'error') return last;
        if (Date.now() >= deadline) return last;
        await sleep(intervalMs, opts.abortSignal);
    }
    return last;
}

/** Resolve after `ms`, or immediately if `signal` is/becomes aborted. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        if (signal?.aborted) { resolve(); return; }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        function onAbort() { clearTimeout(timer); resolve(); }
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

// ── Dashboard popup checkout ──────────────────────────────────────────────────
// Stripe hosted Checkout frame-busts, so inside the dashboard iframe we open it
// in a popup window (a separate top-level browsing context) and confirm payment
// by polling in place rather than navigating the iframe. The standalone site
// keeps the full-page redirect (see the wizards' `isDashboardContext()` branch).

export type PopupCheckoutResult =
    | { status: 'complete'; proofToken: string; registrationId: string }
    | { status: 'cancelled' }
    | { status: 'error'; error: string };

/**
 * Create a Checkout session, point an already-open popup at it, and poll until
 * the worker mints the proof.
 *
 * The popup MUST be opened synchronously by the caller's click handler — BEFORE
 * any `await` — so the browser keeps the user-activation that authorises it;
 * this function only navigates it. A `null` popup means the browser blocked it.
 * The popup is closed when polling ends (success, cancel, error, or timeout).
 */
export async function startPopupCheckoutAndAwaitProof(args: {
    popup: Window | null;
    registrationType: RegistrationType;
    nonce: string;
    /** Voucher slots to buy (registration-restructure §7.1). Defaults to 1, which
     *  is what "pay and continue" buys before redeeming it on the spot. */
    quantity?: number;
    /** Bits per voucher (registration-wallets D1) — mandatory on every order. */
    bits: number;
    /** Organization orders only: the founding delegate's bits (§4.13). */
    founderBits?: number;
    /** One entry per slot, minted on this device BEFORE the checkout opened
     *  (§4.3). The webhook creates the locks against these and has nothing to
     *  create them against otherwise. */
    vouchers: VoucherLockSpec[];
    onStatus?: (label: string) => void;
    abortSignal?: AbortSignal;
}): Promise<PopupCheckoutResult> {
    const { popup, registrationType, nonce, quantity, bits, founderBits, vouchers, onStatus, abortSignal } = args;
    if (!popup) {
        return { status: 'error', error: 'Could not open the payment window. Please allow pop-ups for this site and try again.' };
    }

    let created: CreatePaymentResponse;
    try {
        created = await createPayment({
            registrationType,
            nonce,
            quantity,
            bits,
            ...(founderBits !== undefined ? { founderBits } : {}),
            vouchers,
            successUrl: getSuccessUrl(true),
            cancelUrl: getCancelUrl(true),
        });
    } catch (e) {
        try { popup.close(); } catch { /* already closed */ }
        return { status: 'error', error: e instanceof Error ? e.message : 'Could not start payment' };
    }

    // Navigate the popup (its own top-level context) to Stripe hosted Checkout.
    try { popup.location.href = created.checkoutUrl; } catch { /* cross-origin after nav — ignore */ }

    const result = await pollPaymentUntilSettled(created.registrationId, nonce, {
        abortSignal,
        onTick: (r, attempt) => {
            if (r.status === 'pending') onStatus?.(`Waiting for payment confirmation… (check ${attempt})`);
        },
    });

    try { popup.close(); } catch { /* already closed */ }

    // `complete` wins even if the abort fired on the same tick.
    if (result.status === 'complete') {
        return { status: 'complete', proofToken: result.proofToken, registrationId: created.registrationId };
    }
    if (abortSignal?.aborted) {
        return { status: 'cancelled' };
    }
    if (result.status === 'error') {
        return { status: 'error', error: result.error };
    }
    return { status: 'error', error: 'Payment is taking longer than expected. You can retry within the hour.' };
}

// ── Persistence (onboarding stash) ──────────────────────────────────────────
// Keep the encrypted key bag, the public keys, and the THROWAWAY credentials
// across the Stripe redirect AND across tab/browser crashes: the state rides
// the localStorage onboarding stash (auth/onboarding-stash.ts, TTL-bounded),
// wiped at the print gate. See registration-restructure §3.3.

import {
    saveOnboardingStash, loadOnboardingStash, clearOnboardingStash,
} from './onboarding-stash';

export interface PersistedRegistrationState {
    registrationType: 'personal' | 'organization';   // no wizard buys a delegate slot
    registrationId: string;
    nonce: string;
    /** Snapshot of the wizard's `shared` map at the moment of payment. */
    shared: Record<string, unknown>;
    /** Voucher being redeemed, when the wizard took the voucher path. Unlike the
     *  proof token this survives the proof's one-hour TTL, so a resume the next
     *  day still works. */
    voucherCode?: string;
    /**
     * For org registrations, the personal session token must travel with the
     * resumed `/api/organization/register` call as the Authorization header.
     * Captured at payment time so a session timeout during Stripe doesn't break us.
     */
    personalSessionToken?: string;
}

export function persistRegistrationState(state: PersistedRegistrationState): void {
    const prior = loadOnboardingStash();
    saveOnboardingStash({
        v: 1,
        savedAt: Date.now(),
        registrationType: state.registrationType,
        registrationId: state.registrationId,
        nonce: state.nonce,
        voucherCode: state.voucherCode ?? prior?.voucherCode,
        personalSessionToken: state.personalSessionToken,
        // Preserve finalize/reset-card markers a wizard may have written.
        finalizeStarted: prior?.finalizeStarted,
        resetCardSealedToken: prior?.resetCardSealedToken,
        resetCardCreatedAt: prior?.resetCardCreatedAt,
        shared: state.shared,
    });
}

export function restoreRegistrationState(): PersistedRegistrationState | null {
    const stash = loadOnboardingStash();
    if (!stash || !stash.registrationId || !stash.nonce) return null;
    // A delegate activation has no payment leg at all (round 6, §13.3) — it
    // shares the stash record but never a registration state.
    if (stash.registrationType === 'delegate-activation') return null;
    return {
        registrationType: stash.registrationType,
        registrationId: stash.registrationId,
        nonce: stash.nonce,
        shared: stash.shared,
        voucherCode: stash.voucherCode,
        personalSessionToken: stash.personalSessionToken,
    };
}

export function clearRegistrationState(): void {
    clearOnboardingStash();
}

// ── Test hook (dev/e2e only) ────────────────────────────────────────────────
//
// When VITE_PAYMENT_TEST_HOOK is set, the Payment step skips the Stripe
// redirect and mints an ES256 proof locally with the test signing key from
// VITE_PAYMENT_TEST_SIGNING_KEY (ECDSA P-256 private key, PKCS8 DER, base64
// — the e2e harness generates a throwaway keypair and gives the server the
// matching public key as Payment:ProofPublicKey). The Completion step then
// runs as usual against the *real* server, exercising real proof
// verification. Disabled in production builds (Vite dead-code-eliminates the
// branch when both env vars are unset), and useless against a production
// server, which only trusts the payment Worker's key.

/** Unit prices in cents, mirroring the Stripe products the Worker sells
 *  (registration-restructure §7.1 / §13.4). Only the test hook uses these — a real
 *  proof carries whatever Stripe actually charged for the FEE LINE. */
const TEST_HOOK_AMOUNTS: Record<RegistrationType, number> = {
    personal: 2500,
    organization: 10000,
    delegate: 9500,
};

export function isTestHookActive(): boolean {
    return Boolean(import.meta.env.VITE_PAYMENT_TEST_HOOK)
        && Boolean(import.meta.env.VITE_PAYMENT_TEST_SIGNING_KEY);
}

/** Lowercase hex over `bytes` CSPRNG bytes — the proof-id encoding (§14.4). */
function randomHex(bytes: number): string {
    const buf = new Uint8Array(bytes);
    crypto.getRandomValues(buf);
    return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Mint an ES256 proof token locally using the test signing key. Returns the
 * proof token string. Only called when `isTestHookActive()` is true.
 *
 * ROUND 10 (registration-wallets §4.3, §9). The claim set changed shape, and
 * this hook is a WIRE CONTRACT with `PaymentProofService`, not a fixture the
 * server tolerates:
 *
 *   * `amount` is GONE and the server now REFUSES a proof carrying it. With
 *     bits lines in the session the total no longer means "the fee", so the
 *     claim that replaces it, `feeAmount`, is the FEE LINE's total — unit ×
 *     quantity — because the server divides it back out as
 *     `amount = feeAmount / quantity` per voucher row.
 *   * `voucherHashes[]` carries the commitments the buyer's client made BEFORE
 *     the card was charged, exactly `quantity` of them. The hook takes them
 *     from its caller rather than inventing them, so an e2e run exercises the
 *     real client-side derivation all the way through to the row the server
 *     writes.
 *   * There is NO `bits` claim and there never will be: the CID server is blind
 *     to bits (§1 idea 1).
 */
export async function mintLocalTestProof(
    registrationType: RegistrationType,
    nonce: string,
    quantity = 1,
    voucherHashes: string[] = [],
): Promise<string> {
    if (voucherHashes.length !== quantity) {
        // The server refuses this outright. Failing here — before a wizard has
        // spent its payment step — is the difference between a legible test
        // failure and a registration that dies at conversion.
        throw new Error(`voucherHashes must carry exactly ${quantity} entr`
            + `${quantity === 1 ? 'y' : 'ies'}, got ${voucherHashes.length}`);
    }
    const signingKeyB64 = import.meta.env.VITE_PAYMENT_TEST_SIGNING_KEY as string;
    const enc = new TextEncoder();
    const now = Math.floor(Date.now() / 1000);
    // The opaque payment anchor (registration-restructure §14.4). The real Worker
    // derives it as HMAC-SHA256(LIFETIME_SECRET, stripeSessionId) in lowercase
    // hex; there is no Stripe session behind a test-hook proof, so a random value
    // of the SAME SHAPE stands in. Shape matters: the server stores it, derives
    // voucher codes from it and routes refunds on it, so an e2e run must exercise
    // a 64-hex id rather than something `cs_`-prefixed.
    const proofId = randomHex(32);
    const stripeEventId = `evt_test_hook_${freshNonce().slice(0, 24)}`;
    const payload = {
        iss: 'catalog-id-payment-worker',
        proofId,
        stripeEventId,
        registrationType,
        // The FEE LINE's total (§4.3): the session's own total now includes the
        // bits lines, so it is no longer the fee, and the server divides this
        // by `quantity` to write each row's amount.
        feeAmount: TEST_HOOK_AMOUNTS[registrationType] * quantity,
        currency: 'eur',
        paymentMethod: 'card',
        paidAt: now,
        nonce,
        // The server reads the batch size from the SIGNED claim only, so the test
        // hook has to carry it or an e2e "buy 3" would convert into one voucher.
        quantity,
        // One commitment per slot, in mint order — the rows are written in this
        // order too, so `voucherHashes[i]` is row i (§4.3).
        voucherHashes,
        exp: now + 3600,
    };
    const headerB64 = base64UrlEncode(enc.encode(JSON.stringify({ alg: 'ES256', typ: 'JWT' })));
    const payloadB64 = base64UrlEncode(enc.encode(JSON.stringify(payload)));
    const signingInput = `${headerB64}.${payloadB64}`;
    const key = await crypto.subtle.importKey(
        'pkcs8', fromBase64(signingKeyB64) as unknown as ArrayBuffer,
        { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign'],
    );
    // WebCrypto ECDSA emits raw r‖s (IEEE P1363) — exactly the JOSE ES256 wire format.
    const sig = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key, enc.encode(signingInput));
    return `${signingInput}.${base64UrlEncode(new Uint8Array(sig))}`;
}


// ── Voucher purchase (registration-restructure §7.3) ─────────────────────────
//
// Every paid path now runs through here. "Pay and continue" is just
// `buyVouchers({ quantity: 1 })` followed by an immediate redemption, so the
// wizard has exactly one payment code path instead of two, and the e2e test
// hook exercises the real conversion endpoint rather than a shortcut around it.

export type VoucherPurchaseResult =
    | {
        status: 'complete';
        registrationType: RegistrationType;
        quantity: number;
        expiresAt: string;
        /** The organization the batch was recorded against, or null. Non-null only
         *  when an owner/admin delegate bought `delegate` vouchers under their
         *  session (registration-restructure §13.4). */
        organizationCid: string | null;
    }
    | { status: 'cancelled' }
    | { status: 'error'; error: string };

/**
 * Pay for a batch of registration slots and convert the payment into voucher
 * ROWS.
 *
 * ── the codes are the CALLER's ───────────────────────────────────────────────
 * This no longer returns codes, and could not: the buyer's client minted them
 * before the checkout opened (§4.3) and committed only to their hashes, so the
 * node has never seen one and cannot hand any back — on a first conversion or
 * on a replay. The caller passes the `vouchers` it minted and keeps the codes
 * beside them; what comes back is the row metadata (how many, until when, whose
 * ledger).
 *
 * That inversion is what makes the whole lane work: a lock is keyed on a public
 * key derived from a code, so the key has to exist BEFORE the card is charged.
 *
 * The popup, when one is needed, MUST have been opened synchronously by the
 * caller's click handler before any `await` — same user-activation rule as
 * `startPopupCheckoutAndAwaitProof`, which this delegates to. Pass `null` in a
 * standalone browser only if you intend the redirect flow instead; this helper
 * always uses the popup+poll shape because a voucher purchase has no wizard
 * state to resume into.
 *
 * Conversion is idempotent server-side on the proof's opaque payment id, so a
 * lost response can be retried with the same proof and lands on the same rows.
 */
export async function buyVouchers(args: {
    popup: Window | null;
    registrationType: RegistrationType;
    quantity: number;
    /** Bits per voucher (D1). */
    bits: number;
    /** Organization orders only (§4.13). */
    founderBits?: number;
    /** Exactly `quantity` lock commitments, minted by the caller. */
    vouchers: VoucherLockSpec[];
    onStatus?: (label: string) => void;
    abortSignal?: AbortSignal;
}): Promise<VoucherPurchaseResult> {
    const { popup, registrationType, quantity, bits, founderBits, vouchers, onStatus, abortSignal } = args;
    const nonce = freshNonce();

    if (vouchers.length !== quantity) {
        // A mismatch would be refused at `create-payment`, but only after the
        // popup had opened — and the Worker's refusal is not a sentence anybody
        // should have to read.
        try { popup?.close(); } catch { /* ignore */ }
        return {
            status: 'error',
            error: `Expected ${quantity} voucher commitments, got ${vouchers.length}.`,
        };
    }

    let proofToken: string;
    if (isTestHookActive()) {
        // Dev/e2e: skip Stripe, but still go through the real conversion below —
        // and carry the SAME hashes a real checkout would have, so the rows the
        // server writes are the ones this client's codes open.
        try { popup?.close(); } catch { /* ignore */ }
        try {
            proofToken = await mintLocalTestProof(
                registrationType, nonce, quantity, vouchers.map(v => v.codeHash));
        } catch (e) {
            return { status: 'error', error: e instanceof Error ? e.message : 'Could not mint test proof' };
        }
    } else {
        const paid = await startPopupCheckoutAndAwaitProof({
            popup, registrationType, nonce, quantity, bits, founderBits, vouchers, onStatus, abortSignal,
        });
        if (paid.status !== 'complete') return paid;
        proofToken = paid.proofToken;
    }

    onStatus?.('Payment confirmed. Issuing your codes…');
    try {
        // The conversion call carries the session token when one is stored, which is
        // what binds an organization's batch to it — no extra parameter, and no way
        // for a caller to claim an organization it is not a delegate of.
        const batch = await purchaseVouchers({ paymentProofToken: proofToken, nonce, registrationType });
        return {
            status: 'complete',
            registrationType: batch.registrationType,
            quantity: batch.quantity,
            expiresAt: batch.expiresAt,
            organizationCid: batch.organizationCid ?? null,
        };
    } catch (e) {
        // The payment succeeded but conversion did not. The proof is still valid
        // for its hour and conversion is idempotent, so this is retryable — say so.
        return {
            status: 'error',
            error: e instanceof Error
                ? `Payment succeeded but issuing the voucher failed: ${e.message}`
                : 'Payment succeeded but issuing the voucher failed',
            // NOTE: the bits are already parked. The Worker created the locks in
            // the webhook against the keys this client minted, so a conversion
            // failure costs the voucher ROW, never the money — and a retry with
            // the same proof recovers the row.
        };
    }
}
